import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../api';
import Spinner from '../components/Spinner';

interface Subject {
  _id: string;
  name: string;
  description: string;
  slug: string;
  createdAt: string;
}

const StaffSubjects: React.FC = () => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadSubjects = useCallback(async () => {
    try {
      setLoading(true);

      const response = await api.get('/staff/subjects');
      setSubjects(response.data);
    } catch (err: any) {
      setError('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleOpenDialog = (subject?: Subject) => {
    if (subject) {
      setEditingSubject(subject);
      setFormData({
        name: subject.name,
        description: subject.description || '',
        slug: subject.slug
      });
    } else {
      setEditingSubject(null);
      setFormData({
        name: '',
        description: '',
        slug: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSubject(null);
  };

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-');
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: !editingSubject ? generateSlug(name) : formData.slug
    });
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!formData.name || !formData.slug) {
        setError('Name and slug are required');
        return;
      }

      if (editingSubject) {
        await api.put(`/staff/subjects/${editingSubject._id}`, formData);
        setSuccess('Subject updated successfully');
      } else {
        await api.post('/staff/subjects', formData);
        setSuccess('Subject created successfully');
      }

      handleCloseDialog();
      loadSubjects();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save subject');
    }
  };

  const handleDelete = async (subjectId: string) => {
    if (window.confirm('Are you sure you want to delete this subject? This may affect associated materials.')) {
      try {
        await api.delete(`/staff/subjects/${subjectId}`);
        setSuccess('Subject deleted successfully');
        loadSubjects();
      } catch (err: any) {
        setError('Failed to delete subject');
      }
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Manage Subjects</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Subject
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Name</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Slug</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ color: 'var(--on-primary)', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subjects.length > 0 ? (
              subjects.map((subject) => (
                <TableRow key={subject._id}>
                  <TableCell sx={{ color: 'text.primary' }}>{subject.name}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>
                    <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '3px', color: 'inherit' }}>
                      {subject.slug}
                    </code>
                  </TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{subject.description || '—'}</TableCell>
                  <TableCell sx={{ color: 'text.primary' }}>{new Date(subject.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenDialog(subject)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(subject._id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                  No subjects added yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSubject ? 'Edit Subject' : 'Add New Subject'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Subject Name"
            fullWidth
            value={formData.name}
            onChange={handleNameChange}
            margin="normal"
            required
            placeholder="e.g., Biology For Engineers"
          />
          <TextField
            label="Slug"
            fullWidth
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            margin="normal"
            required
            helperText="URL-friendly identifier (auto-generated from name)"
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            placeholder="Optional description of the subject"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingSubject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StaffSubjects;
